import buffer from '@turf/buffer';
import { lineString } from '@turf/helpers';

const toRadians = (deg) => (deg * Math.PI) / 180;
const toDegrees = (rad) => (rad * 180) / Math.PI;

// Circular mean of two headings, so a vertex between two segments gets a
// blended direction instead of just the incoming or outgoing one.
const averageHeading = (a, b) => {
  const x = Math.cos(toRadians(a)) + Math.cos(toRadians(b));
  const y = Math.sin(toRadians(a)) + Math.sin(toRadians(b));
  return toDegrees(Math.atan2(y, x));
};

const headingAt = (path, index) => {
  const point = path[index];
  const prev = path[index - 1];
  const next = path[index + 1];
  const headings = [];
  if (prev) headings.push(google.maps.geometry.spherical.computeHeading(prev, point));
  if (next) headings.push(google.maps.geometry.spherical.computeHeading(point, next));
  return headings.length === 2 ? averageHeading(headings[0], headings[1]) : headings[0];
};

/**
 * Shifts every vertex of a decoded polyline sideways by `distanceMeters`,
 * perpendicular to the path's local heading. `side` is -1 (left of travel
 * direction) or 1 (right). This is a visual approximation, not real road
 * geometry - it doesn't know about medians, ramps or actual carriageways.
 */
export const offsetPath = (path, distanceMeters, side = 1) => {
  if (!path || path.length < 2 || !distanceMeters) return [];
  return path.map((point, index) => (
    google.maps.geometry.spherical.computeOffset(point, distanceMeters, headingAt(path, index) + side * 90)
  ));
};

/**
 * Buffers a decoded polyline path into a polygon corridor `radiusMeters`
 * wide on each side, via turf's buffer() (proper round joins at corners,
 * self-intersection resolved for us). Routes only give a radius, not
 * backend-computed polygon vertices like the old screen's `geofence` field
 * did - a hand-rolled version of this (offsetting each vertex perpendicular
 * to the path and stitching the two sides together) used to be here, but it
 * produced self-intersecting loops/gaps on sharp turns since it didn't
 * handle corner joins at all; turf is a real GIS buffering implementation.
 *
 * Returns an array of rings (each an array of {lat, lng}), since a buffered
 * line can occasionally come back as a MultiPolygon (disconnected parts) -
 * pass this straight to Google Maps Polygon's `paths` prop (plural).
 */
export const bufferPath = (path, radiusMeters) => {
  if (!path || path.length < 2 || !radiusMeters) return [];

  const coordinates = path.map((point) => [point.lng(), point.lat()]);
  let buffered;
  try {
    buffered = buffer(lineString(coordinates), radiusMeters, { units: 'meters' });
  } catch (error) {
    console.log('[TripTrackingV2] geofence buffer error:', error);
    return [];
  }
  if (!buffered || !buffered.geometry) return [];

  const polygons = buffered.geometry.type === 'MultiPolygon'
    ? buffered.geometry.coordinates
    : [buffered.geometry.coordinates];

  // Each part's first ring is its outer boundary - a simple line buffer
  // never produces holes, so only that ring is needed per part.
  return polygons.map((rings) => rings[0].map(([lng, lat]) => ({ lat, lng })));
};
